import {
  Box,
  Stack,
  Button,
  Typography,
  Backdrop,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import { useMemo, useState, useEffect } from "react";
import {
  useOutletContext,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import dayjs from "dayjs";

import ReportSkeleton from "./Skeleton";
import Stats from "./Stats";
import SourceReportTable from "./Table";
import SourceCharts from "./SourceCharts";
import { useSettings } from "../../../context/SettingsContext";
import { exportSourceExcel } from "../../../utils/exportSourceExcel";
import PDF from "./PDF";
import Header from "./Header";
import isoWeek from "dayjs/plugin/isoWeek";
import ServiceCharts from "./ServiceCharts";
import { BuildOverview } from "./BuildOverview";
import Loader from "../Loader";
import NoDataFound from "../NoDataFound";
import { getSourceReportApi } from "../../../api/reports.api";

dayjs.extend(isoWeek);

export default function Report() {
  const { targetMinutes } = useOutletContext();
  const { dynamicStatus, maxSla } = useSettings();
  const [service, setService] = useState("all");

  const [rows, setRows] = useState([]);
  const [reportLoading, setReportLoading] = useState(true);
  const [reportFetchComplete, setReportFetchComplete] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const [renderPdf, setRenderPdf] = useState(false);

  const sourceKey = searchParams.get("source");
  const initialRange = searchParams.get("range") || "day";

  const [range, setRange] = useState(initialRange);
  const [status, setStatus] = useState("all");
  const [sla, setSla] = useState("all");
  const [rangeValue, setRangeValue] = useState([null, null]);

  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [label, setLabel] = useState("");

  const source = sourceKey || "all";

  const cap = (str = "") =>
    str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

  const getStatusLabel = (key) => {
    const s = dynamicStatus?.find((x) => x.key === key);
    return s?.label ?? key ?? "Unknown";
  };

  useEffect(() => {
    const now = dayjs();

    if (range === "day") {
      const s = now.startOf("day");
      const e = now.endOf("day");
      setRangeValue([s, e]);
    }

    if (range === "week") {
      const s = now.startOf("week");
      const e = now.endOf("week");
      setRangeValue([s, e]);
    }

    if (range === "month") {
      const s = now.startOf("month");
      const e = now.endOf("month");
      setRangeValue([s, e]);
    }

    if (range === "custom") {
      setRangeValue([null, null]);
    }
  }, [range]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);

    const hasFrom = params.has("from");
    const hasTo = params.has("to");
    const hasRange = params.has("range");

    if (!hasFrom || !hasTo || !hasRange) {
      const now = dayjs();

      params.set("range", "day");
      params.set("from", now.startOf("day").toISOString());
      params.set("to", now.endOf("day").toISOString());

      navigate({ search: params.toString() }, { replace: true });
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    const from = searchParams.get("from");
    const to = searchParams.get("to");
    if (!from || !to) return;

    const params = {
      from,
      to,
      range: searchParams.get("range") || "day",
      source: searchParams.get("source") || "all",
      status,
      sla,
      service,
      sortBy: "created_at",
      sortOrder: "desc",
      page: 1,
      limit: 5000,
    };

    let alive = true;
    setReportLoading(true);
    setReportFetchComplete(false);

    getSourceReportApi(params)
      .then((res) => {
        if (!alive) return;
        setRows(res.data.data.items || []);
        setReportLoading(false);
        setReportFetchComplete(true);
      })
      .catch(() => {
        if (!alive) return;
        setRows([]);
        setReportLoading(false);
        setReportFetchComplete(true);
      });

    return () => {
      alive = false;
    };
  }, [searchParams, status, sla, service]);

  const hasData = rows.length > 0;

  const exportExcel = async () => {
    setExporting(true);
    setLabel("Generating Sheets");
    setProgress(0);

    await exportSourceExcel({
      leads: rows,
      targetMinutes,
      pipeline_status: dynamicStatus
        ?.filter((s) => s.type === "pipeline")
        .map((s) => s.key),
      grade_status: dynamicStatus
        ?.filter((s) => s.type === "grade")
        .map((s) => s.key),
      fileName: `Source-Performance-Report-${dayjs().format(
        "DD-MM-YYYY"
      )}.xlsx`,
      onProgress: setProgress,
    });

    setExporting(false);
  };

  const dailyOverview = useMemo(
    () =>
      BuildOverview({
        leads: rows,
        from: searchParams.get("from"),
        to: searchParams.get("to"),
        range: searchParams.get("range"),
      }),
    [rows, searchParams]
  );

  const departmentPie = useMemo(() => {
    const map = {};
    rows.forEach((l) => {
      const s = l.custom_fields?.lead_source || "Unknown";
      map[s] = (map[s] || 0) + 1;
    });
    return Object.entries(map).map(([name, value]) => ({
      name: cap(name),
      value,
    }));
  }, [rows]);

  const servicePie = useMemo(() => {
    const map = { Unknown: 0 };

    rows.forEach((l) => {
      const s = l?.services;

      if (Array.isArray(s) && s.length) {
        s.forEach((x) => {
          map[x] = (map[x] || 0) + 1;
        });
      } else if (typeof s === "string" && s) {
        map[s] = (map[s] || 0) + 1;
      } else {
        map.Unknown += 1;
      }
    });

    return Object.entries(map)
      .filter(([, value]) => value > 0)
      .map(([name, value]) => ({
        name,
        value,
      }));
  }, [rows]);

  if (reportLoading || !reportFetchComplete) return <Loader />;

  return (
    <Box sx={{ bgcolor: "#EEF2FF", minHeight: "100vh", p: 2 }}>
      <Header
        parts={["Reports", "Service Report"]}
        theme={theme}
        range={range}
        setRange={setRange}
        rangeValue={rangeValue}
        setRangeValue={setRangeValue}
        searchParams={searchParams}
        navigate={navigate}
        status={status}
        setStatus={setStatus}
        sla={sla}
        setSla={setSla}
        service={service}
        source={source}
        setService={setService}
        exportExcel={exportExcel}
        exportMuiPdf={async ({ elementId, fileName, onProgress }) => {
          setRenderPdf(true);
          setExporting(true);
          setLabel("Generating PDF");
          setProgress(0);
          const { exportMuiPdf } = await import("../../../utils/exportMuiPdf");
          await exportMuiPdf({ elementId, fileName, onProgress });
          setExporting(false);
          setRenderPdf(false);
        }}
        setExporting={setExporting}
        setLabel={setLabel}
        setProgress={setProgress}
      />

      {hasData ? (
        <>
          <Stats rows={rows} getStatusLabel={getStatusLabel} />

          <ServiceCharts
            dailyOverview={dailyOverview}
            servicePie={servicePie}
            departmentPie={departmentPie}
            theme={theme}
          />

          <SourceReportTable
            rows={rows}
            getStatusLabel={getStatusLabel}
            onRowClick={(row) => {
              const type = row?.current_status?.type;

              if (type === "grade") {
                navigate(`/leads/${row._id}`);
                return;
              }

              if (type === "pipeline") {
                navigate(`/pipelines/${row._id}`);
                return;
              }
            }}
          />
        </>
      ) : (
        <NoDataFound />
      )}

      <Backdrop
        open={exporting}
        sx={{
          zIndex: 2000,
          color: "#fff",
          backdropFilter: "blur(10px)",
        }}
      >
        <Stack spacing={2} alignItems="center" sx={{ width: 320 }}>
          <CircularProgress color="inherit" />
          <Typography fontWeight={700}>{label}</Typography>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ width: "100%" }}
          />
          <Typography fontSize={12}>{progress}%</Typography>
        </Stack>
      </Backdrop>

      {renderPdf && (
        <Box
          sx={{
            position: "absolute",
            left: -9999,
            top: 0,
            width: "210mm",
            background: "#fff",
          }}
        >
          <PDF leads={rows} targetMinutes={targetMinutes} />
        </Box>
      )}
    </Box>
  );
}
